// MACHINERY V2 (Stroje) — running program line (M2.1).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §4.3 (LOCKED).
// Reference impl: design_handoff_machinery_v3/machinery-live.jsx (ProgramLine).
//
// Rendered ONLY when the machine is running: guard with the showProgramLine
// flag from deriveCardPresentation() — the caller must not invent its own
// condition. That flag already suppresses the line whenever alarms are present
// (an active alarm outranks it) and keeps it for a STALE running machine,
// whose last-known line is still the truth we have.
//
// Mono format, in order:  ▶ PRG_42.MPF · 247 ks · 11,800 RPM · F 95%
// Trailing segments are dropped when the gateway does not supply them, so a
// partial payload degrades to "▶ PRG_42.MPF · 247 ks" instead of showing
// zeroes it never measured.
//
// Thousands grouping follows the ACTIVE UI language (sk → 11 800,
// en → 11,800) rather than a hard-coded locale.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import StatusIcon from "./StatusIcon";

export interface ProgramLineProps {
  program: string;
  partCount?: number | null;
  spindleRpm?: number | null;
  feedPercent?: number | null;
}

const Dot: React.FC = () => <span style={{ color: "var(--faint)" }}>·</span>;

const ProgramLine: React.FC<ProgramLineProps> = ({
  program,
  partCount = null,
  spindleRpm = null,
  feedPercent = null,
}) => {
  const { translate, getCurrentLanguage } = useTranslation("myMachinery");
  const lang = getCurrentLanguage();

  return (
    <div
      style={{
        padding: "8px 12px",
        display: "flex",
        alignItems: "center",
        gap: 10,
        color: "var(--ink-2)",
        fontFamily: "var(--font-mono)",
        fontSize: 11.5,
        letterSpacing: "0.02em",
        background: "var(--bg-soft)",
        borderTop: "1px solid var(--border-soft)",
        minWidth: 0,
      }}
    >
      <span style={{ color: "var(--green)", display: "inline-flex" }}>
        <StatusIcon kind="play" size={10} />
      </span>
      <span
        style={{
          color: "var(--ink)",
          fontWeight: 600,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {program}
      </span>
      {partCount != null && (
        <>
          <Dot />
          <span style={{ whiteSpace: "nowrap" }}>
            {partCount.toLocaleString(lang)} {translate("myMachinery:v2.pieces")}
          </span>
        </>
      )}
      {spindleRpm != null && (
        <>
          <Dot />
          <span style={{ whiteSpace: "nowrap" }}>
            {spindleRpm.toLocaleString(lang)} RPM
          </span>
        </>
      )}
      {feedPercent != null && (
        <>
          <Dot />
          <span style={{ whiteSpace: "nowrap" }}>F {feedPercent}%</span>
        </>
      )}
    </div>
  );
};

export default ProgramLine;
